import { store } from "../Redux";
import { NavigateFunction } from "react-router-dom";
import Bowser from "bowser";

import { AM_TXN_HISTORY, GLOBAL, SESSION_ID } from "../data/globals";
import * as routes from "../routes/routeConstants";
import { getText } from "../helpers/translateHelper";
import { IBtnStyle, IPaymentOptions, IStyleConfig, PrefixedMsisdn, TMode } from "../config/commonInterfaces";
import { setOfflinePopupFlag } from "../Redux/slices/globalSlice";
import { checkIsBetaFeatureAvailable } from "../helpers/betaHelper";
import { isLoginMsisdnsLenComplete, isLoginMsisdnsLenInLimit } from "../helpers/loginHelper";
import { addSpaceInMsisdn, getDeviceInfo, isPinset, isAMBarred, isResetPin, isLockedUser, isAmAccountActive, isPayXEnabled, isQuestionSet, isAmHistoryDisable, isEmpty } from "../helpers/commonHelpers";
import { getCookieValue, getLocalStorage, getSessionStorage, removeLocalStorage, removeSessionStorage, setLocalStorage, setSessionStorage } from "./localStorage"
import {
  AUTH_TOKEN,
  FORGOT_PIN_TEXT,
  IPHONE,
  IS_GUEST_USER,
  MANAGE_QUESTION,
  PHONE_NUMBER,
  ZERO_DECIMAL,
  EN, LANGUAGE_CAPS, BETA_FEATURES, REGEX_MSISDN_CHECK, CHANGE_PIN, PAY_OPTION_MPESA, headings, MSISDN_LENGTH_COMPLETE_TEXT,
  WIFI,
  HE_MSISDN,
  PREPAID_RECHARGE,
  HE_SUCCESS_FLAG,
  AM_TRANSACTION_HISTORY_BETA_CODE,
} from "../data/constants";
import { clearStore } from "../Redux/actions";
import { callLogout } from "../Redux/slices/appSettingsSlice";
import { updateUserConfig } from "../helpers/userInfoHelper";
import { isPaymentGatewayFeatureEnable } from "../features/PaymentGatewayCard/utils";

export const setPhoneNumberToLs = (value: string) => {
  setLocalStorage(PHONE_NUMBER, value);
  return;
}

export const getPhoneNumberFromLs = () => {
  let number = getLocalStorage(PHONE_NUMBER) || "";
  number = parseMsisdn(number);
  return number;
}

export const getRawPhoneNumber = () => {
  let number = getLocalStorage(PHONE_NUMBER) || "";
  return number;
}

export const deletePhoneNumberFromLs = () => {
  removeLocalStorage(PHONE_NUMBER);
  return;
}

export const setAuthTokenToLs = (value: string) => {
  setSessionStorage(AUTH_TOKEN, value);
  return;
}

export const getAuthTokenFromLs = () => {
  return getSessionStorage(AUTH_TOKEN);
}

export const deleteAuthTokenFromLs = () => {
  removeSessionStorage(AUTH_TOKEN);
  return;
}

export const checkIos = () => {
  const device = getDeviceInfo();
  if (device.OSName === IPHONE)
    return true;
  return false;
}

//make input number in gabon and madagascar atleast 9 digits long by prepending 0's
export const parseMsisdn = (msisdn: string = "") => {
  let number: string = msisdn, opco = process.env.REACT_APP_OPCO;
  //TODO use frm launch config
  const msisdnLengthLim = 9;
  if (!isEmpty(msisdn) && (opco === "GA" || opco === "CG") && number.length < msisdnLengthLim)
    number = `0${msisdn}`;
  return number;
}

export const removeZeroFromStart = (msisdn: string = "") => {
  let number: string = msisdn;
  return number && number.replace(/^0+/, '')
}

export const removePrefix = (msisdn: PrefixedMsisdn) => {
  if (!msisdn)
    return "";
  let resultMsisdn = removeZeroFromStart(msisdn.value);
  const phoneCode = store.getState()?.config?.launchConfig?.phoneCode;
  if (!phoneCode)
    return resultMsisdn;
  return resultMsisdn.startsWith(phoneCode) ? resultMsisdn.substring(phoneCode.length) : resultMsisdn;
}

/**
 * Validate permissible chars and len in input prefixed msisdn
 * The input msisdn might not be having all the characters.
 */
export const validateInputPrefixedMsisdn = (input: string, phoneCode: string, msisdnLength: string) => {
  if (!input)
    return false;

  //only allow numbers and +
  if (/[^+0-9]/g.test(input))
    return false;
  //dont allow 2 zeroes in starting
  if (input.startsWith("00"))
    return false;

  //if starts with +, allow if phone code prefix
  if (input.startsWith("+") && (input.length === +msisdnLength + (phoneCode || "").length) && !input.startsWith(phoneCode)) {
    return false;
  }

  return validatePartialPrefixedMsisdnLen(input, phoneCode, msisdnLength);
}

/**
 * Check if input len <= max len
 * @param input 
 * @param phoneCode 
 * @param msisdnLenLim msisdn len
 */
export const validatePartialPrefixedMsisdnLen = (input: string, phoneCode: string, msisdnLenLim: string) => {

  if (input.startsWith("+") && input.length <= (+msisdnLenLim) + phoneCode.length)
    return true;

  if (input.startsWith("0") && input.length <= (+msisdnLenLim) + 1)
    return true
  return input.length <= +msisdnLenLim;
}

export const validatePrefixedMsisdnLen = (input: string, phoneCode: string, msisdnLenLim: string) => {
  if (input.startsWith("+"))
    return input.length === (+msisdnLenLim) + phoneCode.length;
  else if (input.startsWith("0"))
    return input.length === (+msisdnLenLim) + 1;
  else
    return input.length === +msisdnLenLim;
}

/*
 * Converts a detailed msisdn to UI friendly format
 */
export const getFormattedMsisdn = (msisdn: PrefixedMsisdn) => {
  if (!msisdn)
    return "";
  return addSpaceInMsisdn(removePrefix(msisdn));
}

/** 
 * Get backend compat msisdn
 */
export const getBackendCompatMsisdn = (input: PrefixedMsisdn) => {
  return parseMsisdn(input.value);
}

/**
 * Use to render amount to UI
 * @param amount to be converted into decimal
 * @returns amount with decimal
 */
export const formatAmount = (amount: Number, minimumFractionDigits: number = 2, maximumFractionDigits: number = 2): string => {
  if (!amount)
    return ZERO_DECIMAL;
  return amount.toLocaleString(undefined, { maximumFractionDigits, minimumFractionDigits });
}

export const checkValidBrowser = (checkVersion: boolean = false, isHeBrowser: boolean = false, checkGABrowser: boolean = false) => {
  const supportedBrowsers = store.getState()?.config?.launchConfig?.[`${isHeBrowser ? 'he' : (checkGABrowser ? 'ga' : '')}supportedBrowsers`] || {};
  const bowser = Bowser.getParser(window.navigator.userAgent)
  const browserName = bowser.getBrowserName()?.toLowerCase();
  let status = false

  status = Object?.keys(supportedBrowsers)?.includes(browserName)
  if (status && checkVersion) {
    status = bowser?.satisfies(supportedBrowsers) ?? false
  }
  return status
}

export const checkMinimumScreenSize = () => {
  let isMinimum = false;
  const minimumScreenSize = store.getState().config?.launchConfig?.minimumScreenWidth;
  const screenWidth = window.screen.width;
  isMinimum = screenWidth >= +minimumScreenSize;
  return isMinimum;
}

export const getPhoneCodeAndMsisdn = (msisdn: string) => {
  const phoneCode = store.getState()?.config?.launchConfig?.phoneCode || "";
  return (phoneCode + " " + addSpaceInMsisdn(msisdn));
}

export const isSanitizedInput = (input: string) => {
  const regex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
  const isXSS = regex.test(input);
  return !isXSS;
}

export const getAuthTokenFromCookie = () => (
  getCookieValue("tn")
);

export const getMsisdnFromCookie = () => (
  getCookieValue("ms")
);

export const getHeStatusFromCookie = () => (
  getCookieValue("st")
);

export const getHeFailureMsgFromCookie = () => (
  getCookieValue("flm")
);

export const isWifiConnected = () => {
  let windowObj: any = window;
  let type = windowObj?.navigator?.connection?.type;
  if (type === "wifi")
    return true;
  return false;
};

export const getOpcoDefaultLang = () => {
  const language = process.env.REACT_APP_LANG || EN;
  return language;
}

export const getUserLang = () => {
  const userLang = store.getState()?.home?.userInfo?.userConfig?.userLanguage || "";
  return userLang;
}

export const getLang = () => {
  const lang = getUserLang() || getOpcoDefaultLang()
  return lang
}

export const getLangLabel = (lang: any) => {
  const langOptions = store.getState()?.config?.launchConfig?.languageConfig?.supportedLanguages || [];
  const langInd = langOptions.findIndex((x) => x?.code === lang)
  if (langInd !== -1) return langOptions[langInd]?.label;
  else return lang;
}

export const setIsGuestUser = (value: boolean) => {
  setSessionStorage(IS_GUEST_USER, value);
  return;
}

export const getIsGuestUser = () => {
  return getSessionStorage(IS_GUEST_USER) === "true";
}

export const getAppConfigSettingData = () => {
  const isGuestUser = getIsGuestUser();
  const data = (store.getState()?.config?.launchConfig?.appSettingsConfigs).filter((setting: any, index) => {
    if (!setting?.isDisable) {
      if (isGuestUser) {  // condition to check for guest allowed appsettingconfig
        return setting?.isGuest;
      } else if (isHumburgOptionAllow(setting?.code) && (!setting?.betaCode || checkIsBetaFeatureAvailable(setting?.betaCode))) {
        return setting;
      }
    }
    return false
  });
  return data;
}

export const isHumburgOptionAllow = (code: string) => {
  let status = true;
  const blockedByInvalidPin: any = store.getState().home.amProfile?.blockedByInvalidPin;
  switch (code) {
    case CHANGE_PIN:
    case FORGOT_PIN_TEXT:
      if (!isPinset() || !isQuestionSet() || isAMBarred() || isResetPin() || isLockedUser() || !isAmAccountActive() || !isAmServiceAvailable()) {
        status = false;
      } else if (isPayXEnabled() && blockedByInvalidPin) status = false;
      break;
    case MANAGE_QUESTION:
      if (!isPinset() || !getIsSetQuestionEnabled() || isAMBarred() || isResetPin() || isLockedUser() || !isAmAccountActive() || !isAmServiceAvailable()) {
        status = false;
      } else if (isPayXEnabled() && blockedByInvalidPin) status = false;
      break;
    case LANGUAGE_CAPS:
      const langOptions = store.getState()?.config?.launchConfig?.languageConfig?.supportedLanguages || [];
      if (langOptions?.length < 2) status = false;
      break;
    case AM_TXN_HISTORY:
      if (!store.getState()?.config?.launchConfig?.dashboardConfig?.showAMTransactionHistoryCard || !checkIsBetaFeatureAvailable(AM_TRANSACTION_HISTORY_BETA_CODE) || isAmHistoryDisable())
        status = false;
      break;
    default: break;
  }
  return status;
}

export const isMPESAOptionsAvailable = (paymentObtions: any) => {
  return Boolean(paymentObtions?.find(((el: any) => el?.name === PAY_OPTION_MPESA)));
}


export const isAppSettingFeatureEnable = (featureName: string) => {
  let flag = false;
  (store.getState()?.config?.launchConfig?.appSettingsConfigs).forEach((setting: any, index) => {
    if (setting?.code === featureName && !setting?.isDisable)
      flag = true;
  });
  return flag;
}

export const getAmmountInUSD = (amount: number) => {
  const conversionValue = store.getState()?.config?.launchConfig?.dualCurrency?.usdConversionValue;
  const updatedAmount = amount ? amount / conversionValue : null;
  return updatedAmount ? `${updatedAmount.toFixed(2)}` : '';
}

export const getTotalTransactionFees = (feeData: any) => {
  return +(parseFloat(feeData?.serviceCharge ?? 0) + parseFloat(feeData?.governmentTaxAmount ?? 0) + parseFloat(feeData?.commissionAmt ?? 0)).toFixed(2)
}

export const getTotalAmount = (amount: string, fees: Number) => {
  return formatAmount(+amount + Number(fees));
}

export const isDualCurrency = () => {
  return store.getState().config?.basicConfig?.currency?.length > 1
}

export const getFormattedAmBal = (amBalance: any, currency: string) => {
  let balance = amBalance;
  const showAmBalDecimal = store.getState()?.config?.launchConfig?.showAmBalDecimal;
  if (!showAmBalDecimal) balance = Math.trunc(balance)
  balance = balance?.toLocaleString(
    undefined,
    { maximumFractionDigits: 2 }
  )
  return balance;
}

export const isAmServiceAvailable = () => {
  return store.getState()?.home?.amProfile?.isAmServiceAvailable
}

export const isPWAAppOpen = () => {
  return window?.matchMedia('(display-mode: standalone)')?.matches;
}

export const isAddToHomeFeatureEnabled = (): boolean => {
  const addToHomeConfig = store.getState()?.config?.launchConfig?.addToHomeConfig,
    isEnabledForIOS = addToHomeConfig?.enabledForIOS,
    isEnabledForAndroid = addToHomeConfig?.enabledForAndroid,
    isFeatureEnabled = !!checkIos() ? isEnabledForIOS : isEnabledForAndroid;

  if (isFeatureEnabled && checkIsBetaFeatureAvailable(BETA_FEATURES.ADD_TO_HOME))
    return true;
  return false;
}

export const showAddToHomePopUp = (isDashboard: boolean = false) => {
  const evt = store.getState()?.globalStore?.athsEvent || {},
    isAllowed = !getIsGuestUser() && !isPWAAppOpen() && (!!evt?.platforms?.length || !!checkIos()),
    isFirstTimeUser = store.getState()?.home?.userInfo?.userConfig?.addToHomeFirstSession,
    maybelater = store.getState()?.globalStore?.mayBeLater,
    isDNDEnabled = store.getState()?.home?.userInfo?.userConfig?.saveToHomeDNDEnabled,
    isDesktopView = store.getState().globalStore.isDesktopView;

  //disable popup for desktop view
  if (isDesktopView) return false;

  if (isAddToHomeFeatureEnabled()) {
    if (isDashboard) {
      return isAllowed;
    } else {
      return (isFirstTimeUser ? !maybelater : !isDNDEnabled) && isAllowed;
    }
  } else {
    return false;
  }
}

export const validateAmount = (input: any, curency: string, setAmount: (amount: number | string) => void, errorCallback: (error: string) => void) => {
  const buyAmountLim = store.getState().config?.transactionLimitConfig?.limitConfig?.[PREPAID_RECHARGE];

  const minLimit = buyAmountLim?.minLimit?.[curency] ?? {};

  if (!input) {
    errorCallback('')
    setAmount("")
    return;
  } else if (+input < +minLimit) {
    errorCallback(getText(headings.AMOUNT_SHOULD_BE_GREATER_EQ, `${curency} ${formatAmount(+minLimit)}`));
  } else {
    errorCallback("");
  }
  setAmount(input)
}

export const selfUser = (input?: string) => {
  const loggedInNumber = getPhoneNumberFromLs() || "";
  const isGuestUser = getIsGuestUser();

  if (loggedInNumber === parseMsisdn(input) && !isGuestUser) return true
  else return false
}

export const checkUserAttributeEnable = () => {
  const isUserAttributeEnabled = store.getState().config?.launchConfig?.webEngageConfig?.isUserAttributeEnabled;
  return isUserAttributeEnabled;
}

export const isWebengageEventsEnabled = () => store.getState().config?.launchConfig?.webEngageConfig?.isEventsEnabled ?? false;

export const checkMsisdnIsValid = (value: string) => {
  const msisdnLength = store.getState().config?.launchConfig?.msisdnLength;
  const regxFn = new RegExp(REGEX_MSISDN_CHECK)
  if (!isLoginMsisdnsLenInLimit(value, +msisdnLength) || !regxFn.test(value))
    return false;

  if (isLoginMsisdnsLenComplete(value, +msisdnLength)) {
    return MSISDN_LENGTH_COMPLETE_TEXT
  }
  return true
}

export const getPercentageBasedOnLength = (length: number) => {
  return 100 / length;
}

export const isNonBetaScreen = () => {
  return window.location.pathname === routes.LOGIN_ROUTE;
}

export const getOfflinePopupFlag = () => {
  const isOnline = store?.getState?.().globalStore?.isOnline;
  const { isEnable, betaCode, } = store?.getState?.().config?.launchConfig?.offlineConfig ?? {}
  const { HE_Error, LOGIN_ROUTE, LOG_OUT, IN_COMPATIBLE, HOME_PAGE, balanceRoute: { BALANCE }, DEFAULT_ROUTE } = routes
  const pathname = window.location.pathname;

  if (!isOnline && isEnable && (isNonBetaScreen() || checkIsBetaFeatureAvailable(betaCode))) {
    const isNotAloowedRoutes = [HE_Error, LOGIN_ROUTE, LOG_OUT, IN_COMPATIBLE, HOME_PAGE, BALANCE, DEFAULT_ROUTE].some(i => pathname === i)
    return !isNotAloowedRoutes
  } else {
    return false
  }
}

export const checkAndUpdateOfflinePopupFlag = () => {
  const isOnline = store?.getState?.().globalStore?.isOnline;
  const { isEnable, betaCode } = store?.getState?.().config?.launchConfig?.offlineConfig ?? {}
  const shouldSkipOfflineCheck = isOnline || !isEnable;

  if (shouldSkipOfflineCheck) {
    return true
  }
  if (isNonBetaScreen() || checkIsBetaFeatureAvailable(betaCode)) {
    store.dispatch(setOfflinePopupFlag(true))
    return false
  }
  return true
}

export const isMobileDevice = () => {
  const userAgentDetails = Bowser.parse(window.navigator.userAgent)
  return userAgentDetails?.platform?.type === GLOBAL.MOBILE_TEXT
}

export const isTabletDevice = () => {
  const userAgentDetails = Bowser.parse(window.navigator.userAgent);
  const width = window.screen.width;
  const maxTabletScreenWidth = store.getState().config?.launchConfig?.maxScreenWidthOfTablet;
  return userAgentDetails?.platform?.type === GLOBAL.TABLET_TEXT || (userAgentDetails?.platform?.type !== GLOBAL.MOBILE_TEXT && width <= maxTabletScreenWidth)
}

export const getIsSetPinEnabled = () => {
  return store.getState().config?.launchConfig?.enableSetPin && checkIsBetaFeatureAvailable(BETA_FEATURES.SET_PIN)
}

export const getIsSetQuestionEnabled = () => {
  return store.getState().config?.launchConfig?.enableSetQuestion && checkIsBetaFeatureAvailable(BETA_FEATURES.SET_QUESTION)
}

export const isDarkModeAllow = () => {
  const { isEnable = true, betaCode = '' } = store.getState().config?.launchConfig?.darkModeConfig ?? {};
  if (getIsGuestUser()) {
    return false
  }
  return isEnable && checkIsBetaFeatureAvailable(betaCode)
}

export const getDarkThemeColorForIcn = () => {
  const darkModeEnabled = store.getState().home.userInfo?.userConfig?.darkModeEnabled
  return darkModeEnabled && isDarkModeAllow() ? '#FFFFFF' : undefined
}
export const handleClearStoreAndToken = () => {
  deleteAuthTokenFromLs();
  store.dispatch(clearStore())
}

export const handleLogout = (navigate: NavigateFunction) => {
  store.dispatch(callLogout({
    callback: (res: any) => {
      deleteAuthTokenFromLs();
      deletePhoneNumberFromLs();
      // handleClearStoreAndToken()
      navigate(routes.LOG_OUT, { replace: true });
    }
  }));
};

export const checkPathToResetData = (): boolean => {
  const pathname = window.location.pathname;
  const isGuestUser = getIsGuestUser();
  return !getAuthTokenFromLs() && ![routes.HE_Error, routes.LOG_OUT].some((path: string) => pathname === path) && !isGuestUser;
}


export function getNetworkType(): string {
  // Check if navigator.connection is supported
  if ('connection' in navigator) {
    const connection = navigator.connection as { effectiveType?: string, type?: string };
    // Check if the connection type is explicitly 'wifi'
    if (connection.type === WIFI) {
      return WIFI;
    }
    return connection.effectiveType || 'unknown';
  }
  return 'unknown';
}

export const handleConditionalLogout = (navigate: NavigateFunction, triggerLogout = () => { }) => {
  if (store?.getState()?.home?.userInfo?.userConfig?.addToHomeFirstSession)
    updateUserConfig({ addToHomeFirstSession: false }, triggerLogout, triggerLogout, false);
  else
    handleLogout(navigate)
}

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const convertPercentageToValue = (percentage: number, amount: number) => {
  return ((percentage / 100) * (amount)).toFixed(2)
}

export const getUIMode = (): TMode => {
  const darkModeEnabled = store?.getState()?.home?.userInfo?.userConfig?.darkModeEnabled;
  return (isDarkModeAllow() && darkModeEnabled) ? GLOBAL.DARK as TMode : GLOBAL.LIGHT as TMode;
}

export const getStyleConfig = (config: IBtnStyle): { label?: string, style: IStyleConfig } => {
  const uiMode: TMode = getUIMode(),
    { colorConfig, label, margin = "", padding = "", fontSize } = config ?? {},
    { backgroundColor = "", textColor = "" } = colorConfig?.[uiMode] ?? {},
    style = { backgroundColor, color: textColor, margin, padding, fontSize };
  return { label, style }
}

export const setHEMsisdnToLs = (value: string) => {
  setLocalStorage(HE_MSISDN, value);
  return;
}

export const getHEMsisdnFromLs = () => {
  let number = getLocalStorage(HE_MSISDN);
  return number ?? '';
}

export const getAutoLogin = () => {
  let isSuccess = getLocalStorage(HE_SUCCESS_FLAG);
  return isSuccess;
}
export const setIsAutoLogin = (value: boolean) => {
  setLocalStorage(HE_SUCCESS_FLAG, value);
  return;
}

export const clearIsAutoLogin = () => {
  removeLocalStorage(HE_SUCCESS_FLAG)
  return
}

export const openDialerWithNumber = (link: string, target: string = "_self"): void => {
  if (checkAndUpdateOfflinePopupFlag()) {
    window.open(link, target);
  }
}

export function getBaseFilename(url: string): string {
  // Extract the file name and remove .svg or .png extension
  const fileNameWithExtension = url.split('/').pop();

  if (!fileNameWithExtension) {
    throw new Error("Invalid URL or empty file name.");
  }

  // Remove .svg or .png extension
  const baseName = fileNameWithExtension.replace(/\.(svg|png)$/i, '');

  return baseName;
}

export function isDarkModeEnabled(): boolean {
  const darkModeEnabled = store.getState().home.userInfo?.userConfig?.darkModeEnabled;
  return darkModeEnabled && isDarkModeAllow();
}

export const getOPCOName = () => {
  return process.env.REACT_APP_OPCO ?? "";
}

export const checkSmallDevice = () => {
  const windowObj: any = window,
    smallDeviceConfig = store.getState().config?.launchConfig?.smallDeviceConfig;
  return windowObj.innerWidth < smallDeviceConfig?.width && windowObj.innerHeight < smallDeviceConfig?.height;
}

export const isSmallDevice = () => {
  return store.getState().globalStore?.isSmallDevice;
}

export const getPaymentGatewayMethods = (options: IPaymentOptions[] = []) => {
  return options.filter(option => isPaymentGatewayFeatureEnable(option.name));
};

export function isDesktopViewEnable() {
  const responsiveModeEnable = store.getState().config.launchConfig.responsiveModeEnable;
  if (!responsiveModeEnable)
    return isMobileDevice();
  else
    return responsiveModeEnable;
}

export const generateSessionID = () => {
  let d = new Date().getTime();
  let d2 = (performance && performance.now && (performance.now() * 1000)) || 0;
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      let r = Math.random() * 16;
      if (d > 0) {
          r = (d + r) % 16 | 0;
          d = Math.floor(d / 16);
      } else {
          r = (d2 + r) % 16 | 0;
          d2 = Math.floor(d2 / 16);
      }
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
  return uuid;
}

export const setSessionId = () => {
  if (!getSessionStorage(SESSION_ID)) {
    setSessionStorage(SESSION_ID, generateSessionID());
  }
}

export const getBrowserDetails = () => {
  const browser = Bowser.getParser(window?.navigator?.userAgent)?.getBrowser();
  return {
    name: browser.name || "",
    version: browser.version || "",
  }
}